import { useEffect, useSyncExternalStore } from 'react'

/**
 * Mirror the visual viewport into CSS variables, but only while a text field
 * has focus. iOS does not shrink the layout viewport for the keyboard, so
 * `position: fixed; bottom: 0` ends up underneath it; sheets size themselves
 * from --vv-height/--vv-top instead, and anything pinned to the bottom adds
 * --vv-gap. Once the keyboard is gone the variables
 * go straight back to the defaults: Safari sometimes reports a stale offset
 * after dismissal, which used to leave sheets and the tab bar mid-screen.
 */
const isTextField = (el: Element | null) =>
  !!el &&
  (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || (el as HTMLElement).isContentEditable) &&
  !['checkbox', 'radio', 'button', 'submit', 'range'].includes((el as HTMLInputElement).type ?? '')

let keyboardOpen = false
let keyboardUp = false
const listeners = new Set<() => void>()
const notify = () => listeners.forEach((l) => l())
const setKeyboard = (open: boolean) => {
  if (keyboardOpen === open) return
  keyboardOpen = open
  notify()
}
const setKeyboardUp = (up: boolean) => {
  if (keyboardUp === up) return
  keyboardUp = up
  notify()
}
/** the tallest window we have seen with no keyboard: the yardstick for "it shrank" */
let tallest = 0

/**
 * True only when an on-screen keyboard is actually taking space, by either
 * route: the page was resized for it, or the visual viewport shrank under it.
 * A focused field on a laptop is not a keyboard, so anything that moves out of
 * the way should use this rather than focus.
 */
export const useKeyboardUp = () =>
  useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => keyboardUp,
    () => false,
  )

/** True while a text field is focused (the on-screen keyboard is up on a phone). */
export const useKeyboardOpen = () =>
  useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => keyboardOpen,
    () => false,
  )

/**
 * iOS in standalone mode shrinks the layout viewport for the keyboard and,
 * when the keyboard is taken away behind the app's back (switching apps with
 * a field focused, the system dismissing it), sometimes never grows it back:
 * fixed bars sit mid-screen with content drawn underneath. Dropping focus and
 * poking the document's height makes WebKit re-measure.
 */
const nudgeLayout = () => {
  const active = document.activeElement as HTMLElement | null
  if (isTextField(active)) active?.blur()
  const root = document.documentElement.style
  // keep the reader where they were: the old nudge scrolled to the top, which was its own jump
  const x = window.scrollX
  const y = window.scrollY
  root.setProperty('min-height', '100.01vh')
  window.scrollTo(x, y + 1)
  requestAnimationFrame(() => {
    root.removeProperty('min-height')
    window.scrollTo(x, y)
  })
}

/**
 * The tab bar stuck mid-screen. iOS can keep the layout viewport shrunk for a
 * keyboard that has gone (a field removed with its screen takes the keyboard
 * with it and no blur comes), and fixed bars then sit where the keyboard's top
 * was. What iOS reports about its own viewport is not to be trusted here, so
 * this measures the bar: in the installed app on a phone, with no keyboard, its
 * bottom belongs at the page's bottom less its own inset. The page's bottom is
 * the window's (innerHeight), never the screen's: iOS sometimes gives the
 * installed app a page one status bar shorter than the screen (873 of 932 on a
 * Pro Max), and a bar measured against the screen was then pushed down by
 * exactly that much, off the page. That short page is reported on its own. Any shortfall goes
 * into `--layout-lag`, which `--tabbar-bottom` subtracts, so the bar and every
 * bar placed from it move down to where they belong; WebKit is also asked to
 * re-measure (keeping the scroll), and the event goes to Activity once a minute
 * with the numbers, so a phone that still misbehaves can be read afterwards.
 */
const standalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true
let lastNudge = 0
const lastReport: Record<string, number> = {}
const report = (message: string, action = 'layout_stuck', every = 60_000) => {
  const now = Date.now()
  if (now - (lastReport[action] ?? 0) < every) return
  lastReport[action] = now
  void import('@/runtime')
    .then(({ api }) => api.auditPost({ level: 'warn', area: 'device', action, message }))
    .catch(() => {})
}
/**
 * iOS's elastic scrolling: pulled past either end, the whole page, the tab bar
 * with it, follows the finger and springs back. Measured then, the bar reads as
 * stuck; correcting it fights the spring and the screen jumps. So nothing is
 * measured while a finger is down or the page is past its ends, and a scroll is
 * only looked at once it has settled.
 */
let touching = false
const overscrolled = () => {
  const el = document.scrollingElement ?? document.documentElement
  const max = el.scrollHeight - window.innerHeight
  return window.scrollY < 0 || window.scrollY > max + 1
}
/** Look once things have been still for a moment: the one way in for scrolls and viewport changes. */
let settle = 0
const checkSoon = () => {
  window.clearTimeout(settle)
  settle = window.setTimeout(checkStuck, 300)
}
/**
 * The shortfall of the tab bar from its place, in px (0 when it is where it belongs or
 * when nothing can be said). While the visible area is smaller than the page, a keyboard
 * is taking the room (Prime's thread while talking: the field has let go of focus, the
 * keyboard has not yet gone, cards arrive and move the page); the bar is then above it on
 * purpose, and lowering it would drop it and everything placed from `--above-tabbar`
 * (composer, voice bar) under the keyboard.
 */
export const tabbarShortfall = (g: {
  page: number
  bottom: number
  inset: number
  lag: number
  visualHeight: number
  /** kept for the report; the keyboard is read from the height alone (iOS pans the visual viewport down on a scrolled page, which made 932 - 587 - 345 look like no keyboard) */
  visualTop?: number
  /** pinch zoom also shrinks the visual viewport; that is not a keyboard */
  scale?: number
}) => {
  if (g.visualHeight > 0 && (g.scale ?? 1) <= 1.01 && g.page - g.visualHeight > 80) return 0
  const next = Math.round(g.lag + (g.page - g.inset - g.bottom))
  return next > 40 ? next : 0
}
/**
 * The reverse of the above: the keyboard has gone (an app switch, the system dismissing it)
 * and no field has focus, but the window is still the keyboard's height shorter than the
 * screen, so the whole app is drawn too high with black under it. A short page of a
 * status bar's height (59pt on a Pro Max) is the phone's own and is left alone.
 */
export const keyboardLeftBehind = (g: {
  focused: boolean
  full: number
  innerHeight: number
  scale?: number
}) => !g.focused && (g.scale ?? 1) <= 1.01 && g.full - g.innerHeight > 150
const checkStuck = () => {
  if (touching || overscrolled()) return
  const root = document.documentElement.style
  const bar = document.querySelector<HTMLElement>('nav.tabbar')
  // not the installed app, a field in use, or no bar showing (desktop, keyboard up): nothing to correct.
  // (getClientRects, not offsetParent: a fixed element's offsetParent is always null)
  if (!standalone() || isTextField(document.activeElement) || !bar || bar.getClientRects().length === 0) {
    root.removeProperty('--layout-lag')
    return
  }
  // the screen's height the way the phone is held (iOS does not swap screen.width/height);
  // phones only: an iPad window (Stage Manager, Split View) is shorter than its screen on purpose
  const { width, height } = window.screen
  if (Math.min(width, height) > 500) return
  const portrait = window.matchMedia('(orientation: portrait)').matches
  const full = portrait ? Math.max(width, height) : Math.min(width, height)
  // the page's own bottom: the window, which is never taller than the screen
  const page = Math.min(full, window.innerHeight)
  if (full - page > 20) {
    // the installed app given less than the whole screen: nothing here moves for
    // it, but it is worth knowing about (once an hour, with the numbers)
    const top = getComputedStyle(document.documentElement).getPropertyValue('--sat').trim()
    report(
      `page ${full - page}px shorter than the screen (screen ${full}, innerHeight ${window.innerHeight}, visual ${Math.round(window.visualViewport?.height ?? 0)}, offsetTop ${Math.round(window.visualViewport?.offsetTop ?? 0)}${top ? `, safe top ${top}` : ''})`,
      'viewport_short',
      3_600_000,
    )
  }
  const vv0 = window.visualViewport
  if (keyboardLeftBehind({ focused: false, full, innerHeight: window.innerHeight, scale: vv0?.scale })) {
    const now = Date.now()
    if (now - lastNudge > 1000) {
      lastNudge = now
      root.removeProperty('--vv-height')
      root.removeProperty('--vv-top')
      root.removeProperty('--vv-gap')
      setKeyboardUp(false)
      nudgeLayout()
      // look again once WebKit has re-measured
      window.setTimeout(checkStuck, 600)
    }
    report(
      `window ${full - window.innerHeight}px short with no field focused; re-measured (screen ${full}, innerHeight ${window.innerHeight}, visual ${Math.round(vv0?.height ?? 0)}, offsetTop ${Math.round(vv0?.offsetTop ?? 0)}, scale ${vv0?.scale ?? 1})`,
      'keyboard_left_behind',
    )
    return
  }
  const lag = parseFloat(root.getPropertyValue('--layout-lag')) || 0
  // where the bar's bottom is meant to be: its own inset, not counting the lag already applied
  const inset = (parseFloat(getComputedStyle(bar).bottom) || 0) + lag
  const bottom = bar.getBoundingClientRect().bottom
  const vv = window.visualViewport
  const next = tabbarShortfall({
    page,
    bottom,
    inset,
    lag,
    visualHeight: vv?.height ?? 0,
    visualTop: vv?.offsetTop ?? 0,
    scale: vv?.scale,
  })
  if (next > 0) {
    if (Math.abs(next - lag) > 1) root.setProperty('--layout-lag', `${next}px`)
    const now = Date.now()
    if (now - lastNudge > 1000) {
      lastNudge = now
      nudgeLayout()
    }
    report(
      `tab bar ${Math.round(page - inset - bottom)}px above its place; moved by ${next}px (screen ${full}, innerHeight ${window.innerHeight}, visual ${Math.round(vv?.height ?? 0)}, offsetTop ${Math.round(vv?.offsetTop ?? 0)}, scale ${vv?.scale ?? 1})`,
    )
  } else if (lag) root.removeProperty('--layout-lag')
}

export const useVisualViewportVars = () => {
  useEffect(() => {
    const vv = window.visualViewport
    const root = document.documentElement.style
    const reset = () => {
      root.removeProperty('--vv-height')
      root.removeProperty('--vv-top')
      root.removeProperty('--vv-gap')
    }
    const apply = () => {
      if (!isTextField(document.activeElement)) tallest = Math.max(tallest, window.innerHeight)
      const focused = isTextField(document.activeElement)
      setKeyboard(focused)
      checkSoon()
      // mirror only while the visual viewport is actually smaller than the
      // window; a stale full-height reading would pin sheets to the top
      if (!vv || !focused) {
        reset()
        setKeyboardUp(false)
        return
      }
      if (vv.height >= window.innerHeight - 1 && window.innerHeight >= tallest - 80) {
        // focused, but nothing moved: a hardware keyboard, or a desktop browser
        reset()
        setKeyboardUp(false)
        return
      }
      root.setProperty('--vv-height', `${Math.round(vv.height)}px`)
      root.setProperty('--vv-top', `${Math.round(vv.offsetTop)}px`)
      // How far the bottom of the page sits above the bottom of the visible area.
      // The viewport meta asks the browser to resize the page for the keyboard, and
      // where it obliges this is 0: a fixed element is already sitting on the
      // keyboard and must not be lifted again. Where it does not, this is the
      // keyboard's height. Measured, so both behave the same.
      const gap = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
      root.setProperty('--vv-gap', `${gap}px`)
      // it counts as up if the page shrank for it, or if it covers the viewport
      setKeyboardUp(gap > 80 || window.innerHeight < tallest - 80)
    }
    const onFocusIn = () => apply()
    // a focused field can be removed with the sheet it lives in, and that fires
    // no blur in every browser: re-check on the next thing the person does, or
    // the tab bar stays hidden and anything keyed off the keyboard stays wrong
    const recheck = () => {
      if (keyboardOpen && !isTextField(document.activeElement)) apply()
    }
    const onFocusOut = () => {
      // Safari fires blur before the keyboard has gone; settle, then reset,
      // and look again once the keyboard has had time to go (the stuck case)
      setTimeout(apply, 60)
      setTimeout(checkStuck, 450)
    }
    // a scroll is the moment a stuck bar is noticed: check once it has settled
    // (no scroll for a moment, the finger lifted), never mid-bounce
    const onScroll = checkSoon
    const onTouchStart = () => {
      touching = true
    }
    const onTouchEnd = () => {
      touching = false
      onScroll()
    }
    const onReturn = (e?: Event) => {
      // a page shown fresh is not a return (it would drop the focus of the field just tapped)
      if (e?.type === 'pageshow' && !(e as PageTransitionEvent).persisted) return
      if (document.visibilityState !== 'visible') return
      // back from another app or the switcher: assume nothing about the keyboard
      nudgeLayout()
      setTimeout(apply, 120)
      // the keyboard may have gone behind the app's back: look again once it has had time to
      setTimeout(checkStuck, 500)
      setTimeout(checkStuck, 1500)
    }
    apply()
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('pointerdown', recheck, true)
    document.addEventListener('keydown', recheck, true)
    document.addEventListener('focusout', onFocusOut)
    document.addEventListener('visibilitychange', onReturn)
    window.addEventListener('pageshow', onReturn)
    vv?.addEventListener('resize', apply)
    vv?.addEventListener('scroll', apply)
    window.addEventListener('resize', apply)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('touchstart', onTouchStart, { passive: true, capture: true })
    window.addEventListener('touchend', onTouchEnd, { passive: true, capture: true })
    window.addEventListener('touchcancel', onTouchEnd, { passive: true, capture: true })
    return () => {
      window.removeEventListener('resize', apply)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('touchstart', onTouchStart, true)
      window.removeEventListener('touchend', onTouchEnd, true)
      window.removeEventListener('touchcancel', onTouchEnd, true)
      window.clearTimeout(settle)
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('pointerdown', recheck, true)
      document.removeEventListener('keydown', recheck, true)
      document.removeEventListener('focusout', onFocusOut)
      document.removeEventListener('visibilitychange', onReturn)
      window.removeEventListener('pageshow', onReturn)
      vv?.removeEventListener('resize', apply)
      vv?.removeEventListener('scroll', apply)
      reset()
    }
  }, [])
}
